import { useNavigate } from 'react-router-dom';
import StationsTab from '@/Pages/Catalog/StationsTab';
import BranchesPanel from '@/Pages/Settings/components/BranchesPanel';
import StaffPanel from '@/Pages/Settings/components/StaffPanel';
import type { SpacesChain } from '@/Pages/Settings/components/spaces/useSpacesChain';
import type { SetupStep, StepId } from '../useSetupStatus';
import { ElsewhereStep } from './ElsewhereStep';
import { ItemStep } from './ItemStep';
import { SpacesStep } from './SpacesStep';
import { ErrorNotice, ReadOnlyNotice, RestrictedNotice } from './StepNotice';

/**
 * Which steps are done here and which are done on their own screen.
 *
 * Declared as a table rather than left implicit in the switch below, because
 * "is this step a link?" is a product decision somebody has to be able to check
 * at a glance — and because the honest answer for seven of the nine is "no".
 * `ElsewhereStep` carries the reasoning for the two that are.
 */
export const ELSEWHERE: ReadonlySet<StepId> = new Set<StepId>(['menu', 'map']);

/**
 * The working half of a step: the real editor, not a description of one.
 *
 * Every branch of this switch renders a component that already exists and
 * already writes — `BranchesPanel`, `SpaceDialog` through `SpacesStep`,
 * `StationsTab`, `ItemsTab`, `StaffPanel`. None of them is a copy, and none of
 * them defers: each writes to the API as it is used, which is the whole change.
 *
 * `onWrote` never means "this step is done". It means "the venue changed, go and
 * ask again". Deciding done-ness here from the fact that a create dialog closed
 * is precisely how a failed write shows a green tick.
 */
export function StepBody({
  step,
  chain,
  onWrote,
  onReload,
  onBack,
}: {
  step: SetupStep;
  chain: SpacesChain;
  onWrote: () => void;
  onReload: () => void;
  onBack: () => void;
}) {
  const navigate = useNavigate();

  // Unknown, not empty. Rendering a panel whose list request is going to 403
  // would show an empty screen that reads as "nothing here yet".
  if (step.state === 'restricted') return <RestrictedNotice step={step} />;
  if (step.state === 'error') return <ErrorNotice onRetry={onReload} />;

  if (ELSEWHERE.has(step.id)) return <ElsewhereStep step={step} />;

  // `to` is null exactly when this actor cannot reach the write behind the step,
  // so the panel below will render with its Add and Edit buttons withheld.
  const readOnly = step.to === null;

  return (
    <div className="space-y-3">
      {readOnly && <ReadOnlyNotice step={step} />}

      {step.id === 'branch' && <BranchesPanel onChanged={onWrote} />}

      {(step.id === 'floor' || step.id === 'area' || step.id === 'table') && (
        <SpacesStep kind={step.id} chain={chain} onWrote={onWrote} onBack={onBack} />
      )}

      {step.id === 'station' && <StationsTab onChanged={onWrote} />}

      {step.id === 'item' && <ItemStep onWrote={onWrote} />}

      {step.id === 'staff' && (
        <StaffPanel
          onChanged={onWrote}
          // The scope-assignment matrix is a screen of its own and always was;
          // hiring is what this step is for, and it happens here.
          onManageRoles={(userId) =>
            navigate(`/settings/assignments?staff=${encodeURIComponent(userId)}`)
          }
        />
      )}
    </div>
  );
}
